/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */
import { Location } from '@angular/common';
import { Injectable, InjectionToken } from '@angular/core';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { environment } from '@app/environments/environment';
import { isEqual } from 'lodash-es';
import { Observable, map, of } from 'rxjs';
import { delay } from 'rxjs/operators';

export interface PdfAnnotation {
  target: AnnotationTarget;
  annotations: object[]; // Annotation data from pdf.js
  draftAnnotations?: object[];
}

export interface SaveAnnotationRequest {
  target: AnnotationTarget;
  annotations: object[];
  isDraft?: boolean;
}

export type AnnotationsWithDraft = {
  committed?: object[];
  draft?: object[];
};

export type AnnotationTarget = {
  type: 'learnerForm';
  cardUid: string;
  formUid: string;
  userUid: string;
  file: string;
};

export interface PdfAnnotationDataService {
  saveAnnotations(request: SaveAnnotationRequest): Observable<object[]>;
  loadAnnotations(target: AnnotationTarget, includeDraft: boolean): Observable<AnnotationsWithDraft>;
  commitDraft(target: AnnotationTarget): Observable<void>;
  deleteAnnotations(target: AnnotationTarget, deleteDraft?: boolean): Observable<void>;
}

export const PDF_ANNOTATION_DATA_SERVICE = new InjectionToken<PdfAnnotationDataService>('PdfAnnotationDataService');

@Injectable()
export class RealPdfAnnotationDataService implements PdfAnnotationDataService {
  constructor(private restClient: RestClientService) {}

  deleteAnnotations(target: AnnotationTarget): Observable<void> {
    return of(void 0);
  }

  loadAnnotations(target: AnnotationTarget, includeDraft: boolean): Observable<AnnotationsWithDraft> {
    return this.restClient.get<AnnotationsWithDraft>(this.buildAnnotationsUrl(target), { includeDraft }).pipe(map(({ body }) => body));
  }

  saveAnnotations(request: SaveAnnotationRequest): Observable<object[]> {
    return this.restClient.post(this.buildAnnotationsUrl(request.target), request.annotations).pipe(map(() => request.annotations));
  }

  commitDraft(target: AnnotationTarget): Observable<void> {
    return this.restClient.post<void>(`${this.buildAnnotationsUrl(target)}/commit`).pipe(map(() => void 0));
  }

  private buildAnnotationsUrl(target: AnnotationTarget): string {
    switch (target.type) {
      case 'learnerForm':
        return Location.joinWithSlash(
          environment.apiRootUrl || '',
          `/resources/document-annotations/cards/${target.cardUid}/forms/${target.formUid}/users/${target.userUid}/files/${encodeURIComponent(target.file)}`,
        );
      default:
        return '';
    }
  }
}

@Injectable()
export class MockPdfAnnotationDataService implements PdfAnnotationDataService {
  private readonly STORAGE_KEY = 'pdf_annotations';

  saveAnnotations(request: SaveAnnotationRequest): Observable<object[]> {
    return new Observable((observer) => {
      try {
        const annotations = this.getAllFromStorage();
        const existingIndex = annotations.findIndex((a) => isEqual(a.target, request.target));

        const now = new Date();
        const annotation: PdfAnnotation = {
          target: request.target,
          ...(request.isDraft
            ? { draftAnnotations: request.annotations, annotations: annotations[existingIndex]?.annotations ?? undefined }
            : { annotations: request.annotations }),
        };

        if (existingIndex >= 0) {
          annotations[existingIndex] = annotation;
        } else {
          annotations.push(annotation);
        }

        this.saveToStorage(annotations);
        observer.next(annotation.annotations);
        observer.complete();
      } catch (error) {
        observer.error(error);
      }
    });
  }

  loadAnnotations(target: AnnotationTarget): Observable<AnnotationsWithDraft> {
    return new Observable<AnnotationsWithDraft>((observer) => {
      try {
        const annotations = this.getAllFromStorage();
        const found = annotations.find((a) => isEqual(a.target, target));
        observer.next({ committed: found?.annotations, draft: found?.draftAnnotations });
        observer.complete();
      } catch (error) {
        observer.error(error);
      }
    }).pipe(delay(2000));
  }

  commitDraft(target: AnnotationTarget): Observable<void> {
    return new Observable((observer) => {
      try {
        const annotations = this.getAllFromStorage();
        const existingIndex = annotations.findIndex((a) => isEqual(a.target, target));
        if (existingIndex >= 0 && annotations[existingIndex].draftAnnotations) {
          console.log(annotations[existingIndex]);
          annotations[existingIndex] = { ...annotations[existingIndex], annotations: annotations[existingIndex].draftAnnotations };
          console.log(annotations[existingIndex]);
          this.saveToStorage(annotations);
        }
        observer.next();
      } catch (error) {
        observer.error(error);
      }
    });
  }

  deleteAnnotations(target: AnnotationTarget): Observable<void> {
    return new Observable((observer) => {
      try {
        const annotations = this.getAllFromStorage();
        const filtered = annotations.filter((a) => isEqual(a.target, target));
        this.saveToStorage(filtered);
        observer.next();
        observer.complete();
      } catch (error) {
        observer.error(error);
      }
    });
  }

  private getAllFromStorage(): PdfAnnotation[] {
    const data = localStorage.getItem(this.STORAGE_KEY);
    if (!data) {
      return [];
    }
    try {
      return JSON.parse(data);
    } catch {
      return [];
    }
  }

  private saveToStorage(annotations: PdfAnnotation[]): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(annotations));
  }
}
